import { useEffect, useMemo, useState } from 'react';
import { getPcfConfigurationPropertiesGrpc } from '../../../shared/api/grpc/pcfExportClient';
import { useRevitParameterOptions } from '../../../shared/hooks/useRevitParameterOptions';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import type { AvailableParameter } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import { collectFallbackParametersFromSystems, mergeParameters } from '../lib/availableParameters';
import type { PipingSystem } from '../types';

interface UseExportAvailableParametersOptions {
  open: boolean;
  systems: PipingSystem[];
}

interface ScopedConfigurationParameters {
  readonly scopeKey: string;
  readonly parameters: AvailableParameter[];
}

export function useExportAvailableParameters(
  options: UseExportAvailableParametersOptions
): AvailableParameter[] {
  const [configuration, setConfiguration] = useState<ScopedConfigurationParameters | null>(null);
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const activeScopeKey = `${activeDocumentInfo.projectKey ?? ''}|${activeDocumentInfo.modelVersion ?? ''}`;
  const { options: revitParameterOptions } = useRevitParameterOptions({ enabled: options.open });
  const fallbackParameters = useMemo(
    () => collectFallbackParametersFromSystems(options.systems),
    [options.systems]
  );
  const liveParameters = useMemo(
    () => revitParameterOptions.map((parameter) => ({
      name: parameter.value,
      dataType: parameter.dataType ?? 'string',
      category: parameter.group ?? 'Revit parameter',
      isReadOnly: false,
    })),
    [revitParameterOptions]
  );

  useEffect(() => {
    if (!options.open) {
      return;
    }

    setConfiguration(null);
    let cancelled = false;

    void getPcfConfigurationPropertiesGrpc().then((response) => {
      if (cancelled || !response.success) {
        return;
      }

      const nextConfigurationParameters = response.properties.map((property) => ({
        name: property.name,
        dataType: property.dataType,
        category: property.category,
        isReadOnly: property.isReadOnly,
      }));
      setConfiguration({
        scopeKey: activeScopeKey,
        parameters: nextConfigurationParameters,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [activeScopeKey, options.open]);

  const configurationParameters = useMemo(
    () => configuration?.scopeKey === activeScopeKey ? configuration.parameters : [],
    [activeScopeKey, configuration]
  );

  return useMemo(() => {
    if (!options.open) {
      return [];
    }

    return mergeParameters(
      mergeParameters(liveParameters, configurationParameters),
      fallbackParameters
    );
  }, [configurationParameters, fallbackParameters, liveParameters, options.open]);
}
